/**
 * Toast Store - Zustand store for lightweight global toast notifications.
 *
 * Provides a small, app-wide notification queue so the UI can surface
 * success / error / info feedback (e.g. sync completed, sync failed) instead
 * of relying solely on a tiny status dot.
 *
 * Toasts auto-dismiss after {@link TOAST_DURATION_MS}. The `toast` convenience
 * helper lets non-React code (stores, services) trigger toasts without a hook.
 */

import { create } from 'zustand';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ToastType = 'success' | 'error' | 'info';

export interface Toast {
  /** Unique id used as the React key and for removal. */
  id: string;
  /** Visual + semantic category of the toast. */
  type: ToastType;
  /** Human-readable message shown to the user. */
  message: string;
}

export interface ToastState {
  /** The current stack of visible toasts (oldest first). */
  toasts: Toast[];
}

export interface ToastActions {
  /**
   * Add a toast of the given type/message. Generates an id, pushes it onto
   * the stack, and schedules auto-removal after {@link TOAST_DURATION_MS}.
   * Returns the generated id.
   */
  addToast: (type: ToastType, message: string) => string;
  /** Remove a toast by id (e.g. manual dismiss or auto-expiry). */
  removeToast: (id: string) => void;
  /** Remove all toasts. */
  clear: () => void;
}

export type ToastStore = ToastState & ToastActions;

// ─── Constants ───────────────────────────────────────────────────────────────

/** How long a toast stays on screen before auto-dismissing. */
export const TOAST_DURATION_MS = 4000;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Generate a reasonably unique id, preferring crypto.randomUUID when available. */
function generateId(): string {
  if (
    typeof crypto !== 'undefined' &&
    typeof crypto.randomUUID === 'function'
  ) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ─── Store ───────────────────────────────────────────────────────────────────

export const useToastStore = create<ToastStore>((set, get) => ({
  // ── State ──────────────────────────────────────────────────────────────────
  toasts: [],

  // ── Actions ────────────────────────────────────────────────────────────────
  addToast: (type, message) => {
    const id = generateId();
    set({ toasts: [...get().toasts, { id, type, message }] });

    // Auto-dismiss after the configured duration.
    setTimeout(() => {
      get().removeToast(id);
    }, TOAST_DURATION_MS);

    return id;
  },

  removeToast: (id) => {
    set({ toasts: get().toasts.filter((t) => t.id !== id) });
  },

  clear: () => {
    set({ toasts: [] });
  },
}));

// ─── Convenience helpers ───────────────────────────────────────────────────────

/**
 * Imperative helpers so non-React code (stores, services, event handlers) can
 * trigger toasts without using the `useToastStore` hook.
 */
export const toast = {
  success: (message: string) =>
    useToastStore.getState().addToast('success', message),
  error: (message: string) =>
    useToastStore.getState().addToast('error', message),
  info: (message: string) =>
    useToastStore.getState().addToast('info', message),
};
